// Generic in-content topical cluster nav for blog post series.
// Renders a small 4-card ordered grid linking sibling posts to concentrate
// PageRank across a topical hub and give AI answer engines a clean series
// they can enumerate.
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical clusters — each ordered from entry-level to deep-dive.
// Slugs live here so we can render clusters in a deliberate reading order
// (not just "most recent" like RelatedPosts).
export const CLUSTERS = {
  facials: {
    id: "facials",
    testidPrefix: "facial-cluster",
    eyebrow: "The Sacramento facials series",
    heading: "Reading everything on facials in Sacramento",
    intro:
      "Four connected guides from Bella — start anywhere, or read straight through. Each one covers a different question about custom facials at the studio.",
    slugs: [
      "facials-launch-sacramento-esthetician",
      "facials-sacramento-ca",
      "facial-treatment-midtown-sacramento-ca",
      "bellas-facial-treatments-midtown-sacramento",
    ],
    detect: (slug = "", tags = []) => {
      const s = (slug || "").toLowerCase();
      if (s.includes("facial")) return true;
      return (tags || []).some((t) => (t || "").toLowerCase().includes("facial"));
    },
  },
  spaPackages: {
    id: "spa-packages",
    testidPrefix: "spa-packages-cluster",
    eyebrow: "The Sacramento spa packages series",
    heading: "Reading everything on Sacramento spa packages",
    intro:
      "Four connected guides — pick the one that fits where you live, what you need, and how you like to book. Read straight through or hop around.",
    slugs: [
      "best-spa-packages-sacramento-ca",
      "best-spa-package-sacramento",
      "spa-packages-downtown-sacramento",
      "spa-packages-midtown-sacramento",
    ],
    detect: (slug = "", tags = []) => {
      const s = (slug || "").toLowerCase();
      if (s.includes("spa-package")) return true;
      return (tags || []).some((t) => (t || "").toLowerCase() === "spa packages");
    },
  },
  halloween: {
    id: "halloween",
    testidPrefix: "halloween-cluster",
    eyebrow: "The Sacramento Halloween series",
    heading: "The Sacramento Halloween makeup series",
    intro:
      "Four connected guides — the general offer, the citywide picker, and neighborhood-specific versions. Read whichever fits your night.",
    slugs: [
      "halloween-makeup-special-sacramento",
      "sacramento-halloween-makeup-guide",
      "midtown-sacramento-halloween-makeup",
      "downtown-sacramento-halloween-makeup",
    ],
    detect: (slug = "", tags = []) => {
      const s = (slug || "").toLowerCase();
      if (s.includes("halloween")) return true;
      return (tags || []).some((t) => (t || "").toLowerCase() === "halloween");
    },
  },
};

/**
 * Return the cluster key (e.g. "facials", "spaPackages") a post belongs to,
 * or null if it doesn't belong to any. Prefers explicit slug membership
 * over tag-based detection for correctness.
 */
export function detectCluster(slug = "", tags = []) {
  for (const [key, cfg] of Object.entries(CLUSTERS)) {
    if (cfg.slugs.includes(slug)) return key;
  }
  for (const [key, cfg] of Object.entries(CLUSTERS)) {
    if (cfg.detect(slug, tags)) return key;
  }
  return null;
}

export default function PostClusterNav({ currentSlug, clusterKey }) {
  const [posts, setPosts] = useState([]);
  const cfg = CLUSTERS[clusterKey];

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        if (!cancel) setPosts(res.data.posts || []);
      } catch {
        /* silent */
      }
    })();
    return () => { cancel = true; };
  }, []);

  const cluster = useMemo(() => {
    if (!cfg) return [];
    const bySlug = new Map(posts.map((p) => [p.slug, p]));
    return cfg.slugs.map((slug) => bySlug.get(slug)).filter(Boolean);
  }, [posts, cfg]);

  if (!cfg || cluster.length < 2) return null;

  return (
    <nav
      data-testid={`${cfg.testidPrefix}-nav`}
      aria-labelledby={`${cfg.id}-cluster-heading`}
      className="mt-16 rounded-lg border border-[#D9CFA0] bg-[#F2EBD3] p-6 md:p-8"
    >
      <div className="flex items-center gap-2 overline-gold mb-3">
        <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
        <span>{cfg.eyebrow}</span>
      </div>
      <h2
        id={`${cfg.id}-cluster-heading`}
        className="font-serif text-2xl md:text-3xl bb-ink font-light tracking-tight mb-2"
      >
        {cfg.heading}
      </h2>
      <p className="bb-ink-soft text-sm md:text-base font-light leading-relaxed mb-6 max-w-2xl">
        {cfg.intro}
      </p>

      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 list-none">
        {cluster.map((p, idx) => {
          const isCurrent = p.slug === currentSlug;
          const CardTag = isCurrent ? "div" : Link;
          const cardProps = isCurrent
            ? {}
            : {
                to: `/blog/${p.slug}`,
                "data-testid": `${cfg.testidPrefix}-link-${p.slug}`,
              };
          return (
            <li key={p.slug}>
              <CardTag
                {...cardProps}
                aria-current={isCurrent ? "page" : undefined}
                className={[
                  "group relative block h-full rounded-md border p-4 md:p-5 transition-colors",
                  isCurrent
                    ? "border-[#C9A65A] bg-[#EAD584]/25 cursor-default"
                    : "border-[#D9CFA0] bg-[#FAF6E8] hover:border-[#C9A65A] hover:bg-white",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className={[
                      "flex-none w-7 h-7 rounded-full grid place-items-center text-[11px] font-medium tabular-nums",
                      isCurrent
                        ? "bg-[#0F1F4E] text-[#EAD584]"
                        : "bg-[#0F1F4E]/5 bb-ink group-hover:bg-[#0F1F4E] group-hover:text-[#EAD584] transition-colors",
                    ].join(" ")}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <h3
                      className={[
                        "font-serif text-base md:text-lg bb-ink font-light leading-snug",
                        isCurrent ? "" : "group-hover:text-[#C9A65A] transition-colors",
                      ].join(" ")}
                    >
                      {p.title}
                    </h3>
                    {isCurrent ? (
                      <div className="mt-1.5 text-[10px] uppercase tracking-wider text-[#C9A65A] font-medium">
                        You&apos;re reading this one
                      </div>
                    ) : (
                      <p className="mt-1.5 text-xs md:text-sm bb-ink-soft font-light leading-relaxed line-clamp-2">
                        {p.excerpt}
                      </p>
                    )}
                  </div>
                </div>
              </CardTag>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
